/* C.5 Phase 2 — Company Detail.

   Bound to GET /api/data/companies/by-identifier/{value} (handles NSE
   symbol, BSE code, ISIN, identifier_value or Billing-native id
   transparently — see _pick_canonical in routes_data_companies.py).
   Includes the Logo Preview (click to enlarge) and the
   upload / replace / delete affordances (gated by `data:write`). */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, Upload, Trash, ImageBroken, ArrowsOut, X,
    PencilSimple, MagnifyingGlassPlus, ArrowsClockwise, Flag,
    Sun, Moon, Checkerboard,
} from "@phosphor-icons/react";
import { api, formatApiError, API_BASE, authedLogoBlob } from "../../lib/api";
import AuthedLogo from "../../components/AuthedLogo";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const ALLOWED_EXTENSIONS = [".png", ".svg", ".jpg", ".jpeg", ".webp"];
const MAX_LOGO_BYTES = 1_000_000;

// ──────────────────────────────────────────────────────────────────
// Iter 68 (2026-02-10) — Logo preview backdrop toggle.
// Many corporate logos are supplied as white-on-transparent PNGs
// (e.g. ASI Industries Limited). Rendered on a white card they
// become invisible. Operators can now switch the preview tile + the
// enlarge-modal backdrop between Light / Dark / Checker. The choice
// persists in localStorage so the operator's preference sticks
// across companies and sessions.
// ──────────────────────────────────────────────────────────────────
const LOGO_BG_OPTIONS = [
    { key: "light", label: "Light", Icon: Sun, title: "Light background (for dark logos)" },
    { key: "dark", label: "Dark", Icon: Moon, title: "Dark background (for white / light logos)" },
    { key: "checker", label: "Checker", Icon: Checkerboard, title: "Checkerboard — reveals transparent vs opaque pixels" },
];
const LOGO_BG_STORAGE_KEY = "reedos.logoPreviewBg";
const CHECKER_STYLE = {
    backgroundImage:
        "linear-gradient(45deg, #cbd5e1 25%, transparent 25%)," +
        "linear-gradient(-45deg, #cbd5e1 25%, transparent 25%)," +
        "linear-gradient(45deg, transparent 75%, #cbd5e1 75%)," +
        "linear-gradient(-45deg, transparent 75%, #cbd5e1 75%)",
    backgroundSize: "12px 12px",
    backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0",
    backgroundColor: "#f8fafc",
};

function bgClasses(key) {
    if (key === "dark") return "bg-slate-900 border-slate-800";
    if (key === "checker") return "border-slate-300";
    return "bg-slate-50 border-slate-200";
}
function bgStyle(key) {
    return key === "checker" ? CHECKER_STYLE : undefined;
}

/* Iter 79.6 — Logo serve is now authenticated; ``<img src=...>`` no
   longer works for direct URL embedding. Use ``<AuthedLogo>`` which
   fetches via authenticated XHR + blob URL. The cache-bust key is
   still derived from ``bust || lastUpdatedAt`` so a fresh upload
   re-fetches the binary without a page reload. */
function logoBustKey(bust, lastUpdatedAt) {
    return bust || lastUpdatedAt || "";
}

function LogoCard({ company, can, onUploaded, onDeleted }) {
    const fileRef = useRef();
    const [busy, setBusy] = useState(false);
    const [bust, setBust] = useState(0);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    // Iter 68 — persist the operator's preview-backdrop choice across
    // companies + sessions. Lazy-init from localStorage so the first
    // paint already reflects the operator's preference (no FOUC).
    const [previewBg, setPreviewBg] = useState(() => {
        try {
            const v = window.localStorage.getItem(LOGO_BG_STORAGE_KEY);
            return LOGO_BG_OPTIONS.some((o) => o.key === v) ? v : "light";
        } catch {
            return "light";
        }
    });
    const setBg = (key) => {
        setPreviewBg(key);
        try { window.localStorage.setItem(LOGO_BG_STORAGE_KEY, key); } catch { /* ignore */ }
    };
    const filename = company.logo_filename;

    const triggerPick = () => {
        setConfirmDelete(false);
        fileRef.current?.click();
    };

    const onPick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-picking the same filename
        if (!file) return;

        const lower = file.name.toLowerCase();
        const okExt = ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
        if (!okExt) {
            toast.error(`Unsupported file. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`);
            return;
        }
        if (file.size > MAX_LOGO_BYTES) {
            toast.error(`File too large (${(file.size / 1024).toFixed(0)} KB). Max ${MAX_LOGO_BYTES / 1024} KB.`);
            return;
        }

        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/data/admin/companies/${company.id}/logo`,
                fd,
                // NB: do NOT hardcode Content-Type — axios needs to derive
                // the multipart boundary itself. Setting "multipart/form-data"
                // without the boundary is a known footgun that can produce
                // a malformed request body (the body parses, but some
                // edge proxies / WAFs reject it because the boundary token
                // doesn't match the header). Pattern matches the CSV
                // import flow at CompanyImportCsvModal.jsx.
                { headers: { "Content-Type": undefined } }
            );
            setBust(Date.now());
            toast.success(
                data.action === "replace"
                    ? `Replaced — ${data.logo_filename}`
                    : `Uploaded — ${data.logo_filename}`
            );
            onUploaded(data);
        } catch (err) {
            toast.error(formatApiError(err, "Upload failed"));
        } finally {
            setBusy(false);
        }
    };

    const doDelete = async () => {
        setBusy(true);
        try {
            await api.delete(`/data/admin/companies/${company.id}/logo`);
            toast.success("Logo removed");
            setConfirmDelete(false);
            onDeleted();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="logo-card"
            className="rounded-lg border border-slate-200 bg-white p-6 flex flex-col items-center gap-4"
        >
            {filename ? (
                <button
                    type="button"
                    onClick={() => setPreviewOpen(true)}
                    data-testid="logo-preview-trigger"
                    title="Click to enlarge"
                    style={bgStyle(previewBg)}
                    className={`relative h-40 w-40 rounded-md border flex items-center justify-center overflow-hidden group hover:border-slate-400 transition-colors ${bgClasses(previewBg)}`}
                >
                    <AuthedLogo
                        filename={filename}
                        bustKey={logoBustKey(bust, company.last_updated_at)}
                        alt={company.company_name}
                        className="h-32 w-32 object-contain p-2"
                        fallback={<ImageBroken size={32} className="text-slate-400" />}
                    />
                    <span className="absolute bottom-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowsOut size={14} className={previewBg === "dark" ? "text-slate-300" : "text-slate-500"} />
                    </span>
                </button>
            ) : (
                <div
                    data-testid="logo-missing"
                    className="h-40 w-40 rounded-md bg-slate-50 border border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 text-slate-400"
                >
                    <ImageBroken size={32} />
                    <span className="text-xs">No logo set</span>
                </div>
            )}

            {/* Iter 68 — Backdrop toggle. Visible only when a logo is
                present (no point switching backdrops behind the
                "No logo set" placeholder). */}
            {filename && (
                <div
                    data-testid="logo-bg-toggle"
                    role="group"
                    aria-label="Logo preview backdrop"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5"
                >
                    {LOGO_BG_OPTIONS.map(({ key, label, Icon, title }) => {
                        const active = previewBg === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setBg(key)}
                                title={title}
                                aria-pressed={active}
                                data-testid={`logo-bg-${key}`}
                                className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] uppercase tracking-wide transition-colors ${
                                    active
                                        ? "bg-slate-900 text-white"
                                        : "text-slate-600 hover:bg-slate-100"
                                }`}
                            >
                                <Icon size={12} weight={active ? "fill" : "regular"} />
                                {label}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="text-center min-h-[2.5rem]">
                {filename ? (
                    <>
                        <div className="font-mono text-xs text-slate-700" data-testid="logo-filename">{filename}</div>
                        {company.logo_source && (
                            <div className="reedos-eyebrow mt-1" data-testid="logo-source">{company.logo_source}</div>
                        )}
                    </>
                ) : (
                    <div className="text-xs text-slate-400">Upload a PNG / SVG / JPG / WEBP up to 1 MB.</div>
                )}
            </div>

            {can("data:write") && (
                <div className="flex flex-col gap-2 w-full">
                    <input
                        ref={fileRef}
                        type="file"
                        accept={ALLOWED_EXTENSIONS.join(",")}
                        className="hidden"
                        onChange={onPick}
                        data-testid="logo-upload-input"
                    />
                    <Button
                        type="button"
                        size="sm"
                        disabled={busy}
                        onClick={triggerPick}
                        data-testid={filename ? "logo-replace-btn" : "logo-upload-btn"}
                        className="w-full"
                    >
                        <Upload size={14} className="mr-1.5" />
                        {filename ? "Replace logo" : "Upload logo"}
                    </Button>
                    {filename && (
                        confirmDelete ? (
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="destructive"
                                    disabled={busy}
                                    onClick={doDelete}
                                    data-testid="logo-delete-confirm-btn"
                                    className="flex-1"
                                >Confirm delete</Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={busy}
                                    onClick={() => setConfirmDelete(false)}
                                    data-testid="logo-delete-cancel-btn"
                                    className="flex-1"
                                >Cancel</Button>
                            </div>
                        ) : (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={busy}
                                onClick={() => setConfirmDelete(true)}
                                data-testid="logo-delete-btn"
                                className="w-full text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-100"
                            >
                                <Trash size={14} className="mr-1.5" />
                                Remove logo
                            </Button>
                        )
                    )}
                </div>
            )}

            {/* Preview modal — full-size view of the logo for visual QC. */}
            {previewOpen && filename && (
                <div
                    data-testid="logo-preview-modal"
                    role="dialog"
                    className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-6"
                    onClick={() => setPreviewOpen(false)}
                >
                    <div
                        className="relative bg-white rounded-lg p-8 max-w-2xl w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            data-testid="logo-preview-close"
                            onClick={() => setPreviewOpen(false)}
                            className="absolute top-3 right-3 text-slate-400 hover:text-slate-900"
                        >
                            <X size={18} />
                        </button>
                        {/* Iter 68 — apply the same backdrop choice
                            inside the enlarged preview so the
                            white-on-transparent case is visible here too. */}
                        <div
                            style={bgStyle(previewBg)}
                            className={`flex items-center justify-center min-h-[300px] rounded-md border ${bgClasses(previewBg)}`}
                            data-testid="logo-preview-modal-canvas"
                        >
                            <AuthedLogo
                                filename={filename}
                                bustKey={logoBustKey(bust, company.last_updated_at)}
                                alt={company.company_name}
                                className="max-h-96 max-w-full object-contain p-6"
                                fallback={<ImageBroken size={48} className="text-slate-400" />}
                            />
                        </div>
                        {/* Mirror the 3-button toggle inside the modal so
                            operators can flip backdrops without closing
                            the enlarged view. */}
                        <div className="mt-4 flex items-center justify-center">
                            <div
                                role="group"
                                aria-label="Logo preview backdrop"
                                data-testid="logo-bg-toggle-modal"
                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5"
                            >
                                {LOGO_BG_OPTIONS.map(({ key, label, Icon, title }) => {
                                    const active = previewBg === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setBg(key)}
                                            title={title}
                                            aria-pressed={active}
                                            data-testid={`logo-bg-modal-${key}`}
                                            className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] uppercase tracking-wide transition-colors ${
                                                active
                                                    ? "bg-slate-900 text-white"
                                                    : "text-slate-600 hover:bg-slate-100"
                                            }`}
                                        >
                                            <Icon size={12} weight={active ? "fill" : "regular"} />
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="mt-3 text-center">
                            <div className="font-mono text-xs text-slate-500">{filename}</div>
                            {/* Iter 79.6 — Anchor replaced with authenticated
                                download. The serve endpoint requires
                                ``data:logo:download``; an ``<a href>`` in a
                                new tab would 401. We fetch the binary with
                                the bearer token and trigger a save-as. */}
                            <button
                                type="button"
                                onClick={async () => {
                                    const blob = await authedLogoBlob(filename);
                                    if (!blob) return;
                                    const a = document.createElement("a");
                                    a.href = blob.src;
                                    a.download = filename;
                                    a.click();
                                    blob.revoke();
                                }}
                                className="text-xs text-indigo-600 hover:underline"
                                data-testid="logo-preview-download-raw"
                            >
                                Download logo ↓
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function MetaRow({ label, value, mono, testId }) {
    return (
        <div className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100 last:border-b-0">
            <dt className="reedos-eyebrow text-slate-500">{label}</dt>
            <dd
                data-testid={testId}
                className={`col-span-2 text-sm text-slate-900 ${mono ? "font-mono text-xs" : ""}`}
            >
                {value ?? <span className="text-slate-300">—</span>}
            </dd>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────
// 2026-02-08 — Repository Administration P1: per-row operator actions
// (Edit Domain, Re-discover Domain, Re-acquire Logo, Needs Review).
// Wired to four new admin endpoints under
//   /api/data/admin/companies/{id}/ ...
// All require ``data:write``.
// ──────────────────────────────────────────────────────────────────
function OperatorActionsCard({ company, can, onChange }) {
    const canWrite = can("data:write");
    const [editingDomain, setEditingDomain] = useState(false);
    const [domainDraft, setDomainDraft] = useState(company.corporate_domain || "");
    const [busyOp, setBusyOp] = useState(null); // 'save-domain'|'rediscover'|'reacquire'|'review-toggle'
    const protectedManual = (
        company.logo_source === "manual_upload" ||
        company.logo_source === "manual-curated"
    );

    const saveDomain = async () => {
        const trimmed = (domainDraft || "").trim();
        if (!trimmed) {
            toast.error("Enter a domain (e.g. irctc.co.in)");
            return;
        }
        setBusyOp("save-domain");
        try {
            const { data } = await api.patch(
                `/data/admin/companies/${company.id}/domain`,
                { domain: trimmed },
            );
            toast.success(`Domain set to ${data.new_domain}`);
            setEditingDomain(false);
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to update domain"));
        } finally {
            setBusyOp(null);
        }
    };

    const rediscover = async () => {
        if (!confirm(
            "Re-run domain discovery for this company?\n\nThis updates corporate_domain only if a verified domain is found. The logo is NOT changed."
        )) return;
        setBusyOp("rediscover");
        try {
            const { data } = await api.post(
                `/data/admin/companies/${company.id}/rediscover-domain`,
            );
            if (data.new_domain) {
                toast.success(`Discovered → ${data.new_domain}`);
            } else {
                toast.warning(
                    data.skipped_reason || "Discovery did not yield a verified domain",
                );
            }
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err, "Re-discover failed"));
        } finally {
            setBusyOp(null);
        }
    };

    const reacquire = async () => {
        const confirmText = protectedManual
            ? null
            : "Re-acquire logo for this company?\n\nThis will OVERWRITE the existing logo if a higher-quality one is found, or leave it unchanged if not.";
        if (confirmText && !confirm(confirmText)) return;
        setBusyOp("reacquire");
        try {
            const { data } = await api.post(
                `/data/admin/companies/${company.id}/reacquire-logo`,
            );
            if (data.new_source) {
                toast.success(`Re-acquired via ${data.new_source}`);
            } else {
                toast.warning(
                    data.skipped_reason || "No higher-quality logo found",
                );
            }
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err, "Re-acquire failed"));
        } finally {
            setBusyOp(null);
        }
    };

    const toggleNeedsReview = async () => {
        setBusyOp("review-toggle");
        try {
            const next = !company.needs_review;
            await api.patch(
                `/data/admin/companies/${company.id}/needs-review`,
                { needs_review: next },
            );
            toast.success(next ? "Flagged for review" : "Cleared review flag");
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err, "Toggle failed"));
        } finally {
            setBusyOp(null);
        }
    };

    const setReviewStatus = async (status) => {
        setBusyOp(`review-${status || "clear"}`);
        try {
            const { data } = await api.patch(
                `/data/admin/companies/${company.id}/review-status`,
                { review_status: status },
            );
            const label = data.review_status
                ? data.review_status.toUpperCase()
                : "CLEARED";
            toast.success(`Review status → ${label}`);
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err, "Review status update failed"));
        } finally {
            setBusyOp(null);
        }
    };

    if (!canWrite) return null;

    return (
        <div
            data-testid="operator-actions-card"
            className="rounded-lg border border-slate-200 bg-white p-6 mt-6"
        >
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-bold text-slate-900">Operator actions</h2>
                {company.needs_review && (
                    <span
                        data-testid="needs-review-badge"
                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                    >
                        <Flag size={12} weight="fill" /> Needs review
                    </span>
                )}
            </div>

            {/* ── Domain block ── */}
            <div className="mb-5">
                <div className="reedos-eyebrow text-slate-500 mb-2">Corporate domain</div>
                {editingDomain ? (
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={domainDraft}
                            data-testid="domain-edit-input"
                            onChange={(e) => setDomainDraft(e.target.value)}
                            placeholder="e.g. irctc.co.in"
                            className="flex-1 h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 font-mono"
                            autoFocus
                        />
                        <Button
                            size="sm"
                            data-testid="domain-edit-save"
                            disabled={busyOp === "save-domain"}
                            onClick={saveDomain}
                        >
                            {busyOp === "save-domain" ? "Saving…" : "Save"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            data-testid="domain-edit-cancel"
                            disabled={busyOp === "save-domain"}
                            onClick={() => {
                                setEditingDomain(false);
                                setDomainDraft(company.corporate_domain || "");
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <code
                            data-testid="current-domain"
                            className="flex-1 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-sm font-mono text-slate-900"
                        >
                            {company.corporate_domain || (
                                <span className="text-slate-400">— no domain set —</span>
                            )}
                        </code>
                        <Button
                            size="sm"
                            variant="outline"
                            data-testid="domain-edit-button"
                            onClick={() => {
                                setDomainDraft(company.corporate_domain || "");
                                setEditingDomain(true);
                            }}
                            className="gap-1.5"
                        >
                            <PencilSimple size={14} /> Edit
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            data-testid="domain-rediscover-button"
                            disabled={busyOp === "rediscover"}
                            onClick={rediscover}
                            className="gap-1.5"
                        >
                            <MagnifyingGlassPlus size={14} />
                            {busyOp === "rediscover" ? "Discovering…" : "Re-discover"}
                        </Button>
                    </div>
                )}
            </div>

            {/* ── Logo actions ── */}
            <div className="mb-4">
                <div className="reedos-eyebrow text-slate-500 mb-2">Logo acquisition</div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        data-testid="logo-reacquire-button"
                        disabled={busyOp === "reacquire" || protectedManual}
                        onClick={reacquire}
                        className="gap-1.5"
                        title={protectedManual ? "Manual uploads are protected from automated re-acquire" : "Re-run logo acquisition"}
                    >
                        <ArrowsClockwise size={14} />
                        {busyOp === "reacquire" ? "Re-acquiring…" : "Re-acquire logo"}
                    </Button>
                    {protectedManual && (
                        <span
                            data-testid="protected-manual-notice"
                            className="text-xs text-slate-500"
                        >
                            Protected — remove the manual upload first to re-acquire.
                        </span>
                    )}
                </div>
            </div>

            {/* ── Review workflow (4-state) ── */}
            <div>
                <div className="reedos-eyebrow text-slate-500 mb-2">Review status</div>
                <div className="flex items-center gap-2 flex-wrap">
                    {[
                        { value: "pending",  label: "Pending",  cls: "bg-amber-50 text-amber-800 border-amber-200" },
                        { value: "approved", label: "Approved", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
                        { value: "rejected", label: "Rejected", cls: "bg-rose-50 text-rose-800 border-rose-200" },
                        { value: "ignored",  label: "Ignored",  cls: "bg-slate-100 text-slate-700 border-slate-200" },
                    ].map(({ value, label, cls }) => {
                        const isActive = company.review_status === value;
                        return (
                            <Button
                                key={value}
                                size="sm"
                                variant="outline"
                                data-testid={`review-status-${value}`}
                                disabled={busyOp === `review-${value}` || isActive}
                                onClick={() => setReviewStatus(value)}
                                className={
                                    "gap-1.5 " + (isActive
                                        ? `${cls} font-semibold cursor-default`
                                        : "")
                                }
                            >
                                <Flag size={14} weight={isActive ? "fill" : "regular"} />
                                {label}
                            </Button>
                        );
                    })}
                    {company.review_status && (
                        <Button
                            size="sm"
                            variant="ghost"
                            data-testid="review-status-clear"
                            disabled={busyOp === "review-clear"}
                            onClick={() => setReviewStatus(null)}
                            className="text-slate-500"
                        >
                            Clear
                        </Button>
                    )}
                </div>
                {company.review_status_at && (
                    <p className="text-[11px] text-slate-500 mt-2">
                        Last set {company.review_status_at}
                        {company.review_status_by && ` by ${company.review_status_by}`}
                    </p>
                )}
            </div>
        </div>
    );
}

export default function CompanyDetail() {
    const { identifier } = useParams();
    const { can } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    // 2026-02-10 — `repoBackTo` reconstructs the Repository URL the
    // operator was on BEFORE drilling into this detail page. The
    // Repository's row-link passes its current `searchParams.toString()`
    // through React Router `state` (key: `repoSearch`). Reading it
    // back here lets the "Back" affordance restore the exact filter
    // envelope + pagination instead of dumping the operator on the
    // bare `/data/companies` URL with all filters reset.
    //
    // Fallback: direct-loads / refreshes have no `state.repoSearch`,
    // so we navigate to `/data/companies` with no params (the
    // historical behaviour, preserved for these edge cases).
    const repoBackTo = useMemo(() => {
        const s = location.state?.repoSearch;
        return s ? `/data/companies?${s}` : "/data/companies";
    }, [location.state]);

    const load = useCallback(async () => {
        if (!identifier) return;
        setLoading(true);
        setNotFound(false);
        try {
            const { data } = await api.get(
                `/data/companies/resolve/${encodeURIComponent(identifier)}`
            );
            setCompany(data);
        } catch (err) {
            if (err?.response?.status === 404) {
                setNotFound(true);
            } else {
                toast.error(formatApiError(err, "Failed to load company"));
            }
        } finally {
            setLoading(false);
        }
    }, [identifier]);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div data-testid="company-detail-loading" className="p-12 text-center text-slate-400">
                Loading…
            </div>
        );
    }

    if (notFound || !company) {
        return (
            <div data-testid="company-detail-notfound" className="p-12 text-center space-y-3">
                <div className="text-slate-500">No company matched <span className="font-mono">{identifier}</span>.</div>
                <Button onClick={() => navigate(repoBackTo)} variant="outline" size="sm">
                    <ArrowLeft size={14} className="mr-1.5" /> Back to repository
                </Button>
            </div>
        );
    }

    return (
        <div data-testid="company-detail-page">
            <PageHeader
                eyebrow="Data · Company Repository"
                title={company.company_name}
                description={[
                    company.nse_symbol && `NSE: ${company.nse_symbol}`,
                    company.bse_code && `BSE: ${company.bse_code}`,
                    company.isin && `ISIN: ${company.isin}`,
                ].filter(Boolean).join(" · ")}
                actions={
                    <Link to={repoBackTo}>
                        <Button variant="outline" size="sm" data-testid="back-to-list">
                            <ArrowLeft size={14} className="mr-1.5" /> Back
                        </Button>
                    </Link>
                }
            />

            <div className="px-6 sm:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left — Logo card */}
                <div className="lg:col-span-1">
                    <LogoCard
                        company={company}
                        can={can}
                        onUploaded={load}
                        onDeleted={load}
                    />
                </div>

                {/* Right — Metadata */}
                <div className="lg:col-span-2">
                    <div className="rounded-lg border border-slate-200 bg-white p-6">
                        <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Identity</h2>
                        <dl>
                            <MetaRow label="ID" value={company.id} mono testId="meta-id" />
                            <MetaRow label="NSE symbol" value={company.nse_symbol} mono testId="meta-nse" />
                            <MetaRow label="BSE code" value={company.bse_code} mono testId="meta-bse" />
                            <MetaRow label="ISIN" value={company.isin} mono testId="meta-isin" />
                            <MetaRow label="Identifier" value={company.identifier_type && `${company.identifier_type}: ${company.identifier_value}`} mono testId="meta-identifier" />
                            <MetaRow label="Reedos comp id" value={company.reedos_comp_id} mono testId="meta-reedos" />
                        </dl>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white p-6 mt-6">
                        <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Classification</h2>
                        <dl>
                            <MetaRow label="Asset type" value={(company.asset_type || "").replace(/_/g, " ")} testId="meta-asset-type" />
                            <MetaRow label="Tier" value={company.tier} testId="meta-tier" />
                            <MetaRow label="Sector" value={company.sector} testId="meta-sector" />
                            <MetaRow label="Domain" value={company.corporate_domain} mono testId="meta-domain" />
                            <MetaRow
                                label="Active"
                                value={company.active
                                    ? <span className="text-emerald-700">Active</span>
                                    : <span className="text-slate-500">Inactive</span>}
                                testId="meta-active"
                            />
                        </dl>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white p-6 mt-6">
                        <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Logo</h2>
                        <dl>
                            <MetaRow label="Filename" value={company.logo_filename} mono testId="meta-logo-filename" />
                            <MetaRow label="Format" value={company.logo_format} testId="meta-logo-format" />
                            <MetaRow label="Source" value={company.logo_source} testId="meta-logo-source" />
                            {/* Iter 79.6 — Was "Public URL". Renamed to
                                "Serve path" and rendered as a non-clickable
                                mono string because the endpoint is now
                                authenticated; opening the URL in a new tab
                                would 401. Operators who need to grab the
                                binary use the "Download logo" button in the
                                preview modal (which fetches with the bearer
                                token attached). */}
                            <MetaRow
                                label="Serve path"
                                value={company.logo_url && (
                                    <span className="font-mono text-xs text-slate-700" data-testid="logo-serve-path">
                                        {company.logo_url}
                                        <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-500">
                                            (authenticated)
                                        </span>
                                    </span>
                                )}
                                testId="meta-logo-url"
                            />
                            <MetaRow label="Last updated" value={company.last_updated_at} mono testId="meta-last-updated" />
                            <MetaRow
                                label="Last enrichment"
                                value={company.last_enrichment_attempt_at && (
                                    <>
                                        <span className="font-mono">{company.last_enrichment_attempt_at}</span>
                                        {company.last_enrichment_result && (
                                            <span className="ml-2 inline-block text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                                                {company.last_enrichment_result}
                                            </span>
                                        )}
                                    </>
                                )}
                                testId="meta-last-enrichment"
                            />
                        </dl>
                    </div>

                    <OperatorActionsCard
                        company={company}
                        can={can}
                        onChange={load}
                    />
                </div>
            </div>
        </div>
    );
}
